import { Nullable } from 'clickNgo/models/utils'
import { getTokens } from 'core/classes/helpers/StorageHelper'
import settings from 'src/config/settings'

const fileDownloadPathPattern = /^\/form\/\d+\/links(?:\/|$)/

const getTrustedFileDownloadUrl = (url: string) => {
  const currentOrigin = window.location.origin
  const allowedOrigins = new Set([currentOrigin])

  try {
    const dataServerUrl = new URL(settings.dataServerUrl, currentOrigin)
    if (dataServerUrl.protocol === 'http:' || dataServerUrl.protocol === 'https:') {
      allowedOrigins.add(dataServerUrl.origin)
    }
  } catch {
    // An invalid optional data-server setting must not expand the allowlist.
  }

  try {
    const parsedUrl = new URL(url, currentOrigin)
    const usesHttp = parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
    const hasCredentials = parsedUrl.username !== '' || parsedUrl.password !== ''

    if (
      usesHttp &&
      !hasCredentials &&
      allowedOrigins.has(parsedUrl.origin) &&
      fileDownloadPathPattern.test(parsedUrl.pathname)
    ) {
      return parsedUrl.toString()
    }
  } catch {
    // Invalid URLs are rejected below.
  }

  throw new Error('Unsafe file download URL')
}

const isImageContentType = (contentType: string | null) =>
  typeof contentType === 'string' && contentType.startsWith('image/')

export const downloadAsFile = async (url: string, fileName: string): Promise<Nullable<File>> => {
  const trustedUrl = getTrustedFileDownloadUrl(url)
  const [token] = getTokens()

  const res = await fetch(trustedUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    redirect: 'error',
  })

  if (!res.ok) {
    throw new Error(`Failed to download: ${res.status} ${res.statusText}`)
  }

  const contentType = res.headers.get('Content-Type')
  const isImage = isImageContentType(contentType)

  const blob = await res.blob()
  const type = contentType || blob.type || 'application/octet-stream'

  const normalizedBlob = blob.type ? blob : new Blob([blob], { type })

  return isImage ? new File([normalizedBlob], fileName, { type }) : null
}
